import {
  Button,
  type ButtonProps,
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ProgressBar from '@/components/shared/ProgressBar';
import type { AppointmentType } from '@/features/appointments/utils/AppointmentType';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useOrchestrateAppointmentFlow from '@/hooks/useOrchestrateAppointmentFlow';
import useProgress from '@/hooks/useProgress';
import useSearchedDestinationLabel from '@/hooks/useSearchedDestinationLabel';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import { selectTrvEndLocationForTagging } from '@/lib/tagging/utils';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';

const SelectAppointmentType: React.FC = () => {
  const [tagged, setTagged] = useState(false);
  const { sessionId, sessionSource, flowType } = useSessionStore();
  const tagging = useTagging();
  const selectedAppointmentType = useAppointmentStore(
    (state) => state.selectedAppointmentType,
  );
  const trvEndLocation = useAppointmentStore(selectTrvEndLocationForTagging);
  const searchedDestinationLabel = useSearchedDestinationLabel();

  const [selectedType, setSelectedType] = useState<AppointmentType | null>(
    selectedAppointmentType,
  );

  useLandingPageRedirection();
  useProgress(20);

  const orchestrateAppointmentFlow = useOrchestrateAppointmentFlow();

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (!tagged && sessionSource && sessionId) {
      tagging.tag('view', sessionSource || 'new', {
        page: Page.appointmentOption,
        sessionid: sessionId,
        flow: flowType || '',
        trv_end_location: trvEndLocation,
      });

      setTagged(true);
    }
  }, [sessionId, flowType, sessionSource, tagging.tag, tagged, trvEndLocation]);

  const handleInPersonSelect = () => {
    setSelectedType('in-branch');
  };

  const handlePhoneCallSelect = () => {
    setSelectedType('online');
  };

  const handleClickContinue = () => {
    if (selectedType) {
      orchestrateAppointmentFlow(selectedType);
      if (selectedType === 'in-branch') {
        navigate(`/schedule-appointment/${id}/branch-selection`);
      } else {
        navigate(`/schedule-appointment/${id}/date-selection`);
      }
    }
  };

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={6}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 1,
        }}
      >
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          spacing={0.5}
        >
          <Typography variant='h4'>
            {searchedDestinationLabel
              ? `Let's plan your ${searchedDestinationLabel} trip together!`
              : `Let's plan your trip together!`}
          </Typography>
          <Typography
            variant='b1Regular'
            sx={{
              fontSize: {
                xs: '1.125rem',
              },
            }}
          >
            Choose how you'd like to connect with a AAA travel advisor.
          </Typography>
        </Stack>
        <Stack
          direction='row'
          spacing={{ xs: 1.5, sm: 3 }}
          role='radiogroup'
          aria-label='Select appointment type'
        >
          <CustomTileButton
            startIcon={<Icon iconName='hotel-duotone' />}
            label='In person'
            onClick={handleInPersonSelect}
            selected={selectedType === 'in-branch'}
            data-testid='in-person-button'
          />
          <CustomTileButton
            startIcon={<Icon iconName='mobile-button-duotone' />}
            label='Phone call'
            onClick={handlePhoneCallSelect}
            selected={selectedType === 'online'}
            data-testid='phone-call-button'
          />
        </Stack>
      </Stack>
      <ProgressBar
        buttonDisabled={!selectedType}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

interface CustomTileButtonProps extends ButtonProps {
  selected?: boolean;
}

const CustomTileButton: React.FC<CustomTileButtonProps> = ({
  selected = false,
  ...props
}) => (
  <Button
    variant='tile'
    role='radio'
    aria-checked={selected}
    sx={[
      {
        '&::before': {
          content: 'none',
        },
        backgroundColor: 'white',
        boxShadow:
          '0 -2px 1px 0 rgba(8, 17, 34, 0.04), 0 2px 4px 0 rgba(8, 17, 34, 0.20)',
        borderRadius: '8px',
        '&.MuiButton-tile': {
          width: { xs: 140, sm: 164 },
          borderColor: 'white',
          gap: 1,
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'primary.50',
            borderColor: 'primary.400',
            transform: 'translateY(-1px)',
            boxShadow:
              '0 -4px 2px 0 rgba(8, 17, 34, 0.04), 0 4px 8px 0 rgba(8, 17, 34, 0.25)',
          },
        },
        '& svg': {
          width: 40,
          height: 40,
        },
        '& .MuiTypography-root': {
          fontWeight: 500,
        },
      },
      selected && {
        '&.MuiButton-tile': {
          borderColor: 'primary.main',
          borderWidth: 2,
          backgroundColor: 'primary.50',
          '&:hover': {
            backgroundColor: 'primary.100',
          },
        },
        '& svg': {
          color: 'primary.main',
        },
        '& .MuiTypography-root': {
          fontWeight: 600,
          color: 'primary.main',
        },
      },
    ]}
    {...props}
  />
);

export default SelectAppointmentType;
